// Command Center — canary redesign (Fase 1). Executive Bloomberg-terminal
// grid. All sub-components keep their existing PROPS and dataflow — only
// classNames + layout changed. Backend, endpoints and business logic
// remain untouched.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { FivePartReasoning } from "@/components/FivePartReasoning";
import { DecisionCard } from "@/components/DecisionCard";
import { t } from "@/i18n";
import {
  TrendingUp, Wallet, Lightbulb, AlertTriangle,
  Send, ChevronRight, ChevronDown, Sparkles, Activity, Eye,
  FileText, ArrowRight, Camera, Gauge, Users, Video, MessageSquare,
} from "lucide-react";
import { DecisionTimeline } from "@/components/DecisionTimeline";
import { formatEnum } from "@/utils/formatEnum";
import { formatFetchedAt } from "@/utils/formatFetchedAt";
import { adviceTargetLabel, channelLabelForService } from "@/utils/adviceTargetLabel";

const fmtEUR = (n) =>
  n == null ? null : `€ ${Number(n).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}`;
const _fmtEUR = (n) =>
  n == null ? "€ —" : `€ ${Number(n).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}`;

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [competitorChanges, setCompetitorChanges] = useState([]);
  const [pendingProposals, setPendingProposals] = useState({ count: 0, loaded: false });
  const [askInput, setAskInput] = useState("");
  const [askResponse, setAskResponse] = useState(null);
  const [asking, setAsking] = useState(false);
  const nav = useNavigate();

  const loadSummary = () => {
    setLoadError(null);
    // Loader-guard (P0): niet oneindig hangen. Bij 5xx/timeout tonen we
    // een B1-NL foutbanner + "Opnieuw proberen"-knop.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    api.get("/dashboard/summary", { signal: controller.signal })
      .then((r) => { clearTimeout(timeoutId); setData(r.data); })
      .catch((e) => {
        clearTimeout(timeoutId);
        setLoadError(e?.response?.data?.detail
          || "Er ging iets mis bij het laden. Probeer het opnieuw.");
      });
  };

  useEffect(() => {
    loadSummary();
    const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    api.get(`/competitors/changes?since=${encodeURIComponent(since)}&limit=10`)
      .then((r) => setCompetitorChanges(r.data.items || []))
      .catch(() => setCompetitorChanges([]));
    api.get("/content-proposals")
      .then((r) => {
        const items = r.data.items || [];
        const pending = items.filter((x) =>
          x.status === "DRAFT" || x.status === "REVIEW").length;
        setPendingProposals({ count: pending, loaded: true });
      })
      .catch(() => setPendingProposals({ count: 0, loaded: true }));
  }, []);

  const submitAsk = async (e) => {
    e.preventDefault();
    if (!askInput.trim()) return;
    setAsking(true);
    setAskResponse(null);
    try {
      const { data } = await api.post("/dashboard/ask", { message: askInput });
      setAskResponse(data);
    } catch (err) {
      setAskResponse({
        answer: err?.response?.data?.detail || t("common.error_generic"),
        insufficient_data: false, evidence: [], assumptions: [],
        confidence: "LOW", next_action: null,
      });
    } finally {
      setAsking(false);
    }
  };

  if (loadError) return (
    <AppLayout>
      <div data-testid="dashboard-load-error"
           className="p-6 rounded-xl border border-neg-red/40 bg-neg-red/10 text-sm text-neg-red flex items-center gap-3 flex-wrap">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="flex-1">Er ging iets mis bij het laden. Probeer het opnieuw.</span>
        <button
          type="button"
          onClick={loadSummary}
          data-testid="dashboard-load-retry-btn"
          className="h-8 px-3 rounded-btn bg-neg-red/20 hover:bg-neg-red/30 border border-neg-red/40 text-neg-red text-xs font-semibold">
          Opnieuw proberen
        </button>
      </div>
    </AppLayout>
  );
  if (!data) return <AppLayout><div className="text-text-muted text-sm">{t("common.loading")}</div></AppLayout>;

  const { kpis, next_step, nexora_would_do_today = [], recent_ghost_decisions = [], wallet_pacing, confirmed_competitors, has_ads_report } = data;
  const budget = kpis.monthly_budget || {};
  const topOpp = kpis.top_opportunity;
  const waste = kpis.potential_waste_eur;

  return (
    <AppLayout>
      {/* Page header ------------------------------------------------------ */}
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display text-text-primary tracking-tight" data-testid="dashboard-title">
            {t("dashboard.title")}
          </h1>
          <p className="text-xs text-text-muted mt-1">
            {new Date().toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()}
          </p>
        </div>
        {!data.stats.intake_completed && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-btn border border-warn-amber/40 bg-warn-amber/10 text-warn-amber text-xs">
            <AlertTriangle className="w-3.5 h-3.5" />
            {t("dashboard.intake_incomplete_banner")}
          </div>
        )}
      </div>

      {/* Row 1 — KPI strip ------------------------------------------------ */}
      <KpiStrip
        pacing={wallet_pacing}
        budget={budget}
        openOppsCount={(data.stats?.opportunities_open ?? 0) || (data.open_g4_advices_count ?? 0)}
        ghostToday={nexora_would_do_today.length + (data.top_advices?.length ?? 0)}
        healthScore={kpis.marketing_health_score}
      />

      {/* Pending concepts card — M114 punt 5 ------------------------------ */}
      {pendingProposals.loaded && pendingProposals.count > 0 && (
        <button
          data-testid="dashboard-pending-concepts-card"
          onClick={() => nav("/weekplan#voorstellen")}
          className="w-full mb-4 text-left rounded-card border border-warn-amber/40 bg-warn-amber/10 hover:bg-warn-amber/15 transition-colors duration-exec px-4 py-2.5 flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3">
            <Sparkles className="w-3.5 h-3.5 text-warn-amber" />
            <div>
              <div className="text-[10px] text-warn-amber font-semibold">
                Te beoordelen concepten
              </div>
              <div className="text-xs text-text-primary">
                U heeft <span data-testid="pending-concepts-count" className="font-semibold num">{pendingProposals.count}</span>{" "}
                contentvoorstel{pendingProposals.count === 1 ? "" : "len"} nog niet goedgekeurd.
              </div>
            </div>
          </div>
          <ChevronRight className="w-3.5 h-3.5 text-warn-amber" />
        </button>
      )}

      <PacingCard pacing={wallet_pacing} onNav={() => nav("/wallet")} />

      {/* Row 2 — 5/7 split ----------------------------------------------- */}
      <div className="grid lg:grid-cols-12 gap-4 mb-4">
        <div className="lg:col-span-5 space-y-4">
          <ConfirmedCompetitorsCard
            payload={confirmed_competitors}
            onManage={() => nav("/business-brain")}
            onOpen={() => nav("/business-brain")}
          />
          <CompetitorChangesPanel
            items={competitorChanges}
            onOpenSnapshot={(id) => nav(`/competitors/${id}/snapshots`)}
            onViewAll={() => nav("/business-brain")}
          />
        </div>
        <div className="lg:col-span-7 space-y-4">
          <Panel title={t("dashboard.would_do_today")} icon={Sparkles}
                  testid="dashboard-would-do-panel">
            {(nexora_would_do_today.length + (data.top_advices?.length ?? 0)) === 0
              ? <EmptyState message={has_ads_report
                    ? "Gegevens zijn opgehaald. Adviezen zijn nog niet beschikbaar."
                    : t("dashboard.would_do_empty")} />
              : (
                <ul className="space-y-2" data-testid="would-do-list">
                  {nexora_would_do_today.map((g) => (
                    <WouldDoItem key={g.id} g={g} />
                  ))}
                  {(data.top_advices || []).map((a) => {
                    const zek = ((a.body || {}).zekerheid || "middel");
                    const ZEK_NL = { hoog: "Hoog", middel: "Middel", laag: "Laag" };
                    const channelNL = channelLabelForService(a.service);
                    const tgt = adviceTargetLabel(a.service, a.advice_rule_id, a.target_entity_id);
                    return (
                      <li key={a.identity_key}
                          data-testid={`g4-advice-item-${a.identity_key}`}>
                        <a href={`/ghost-ledger?open=${encodeURIComponent(a.identity_key)}`}
                           data-testid={`g4-advice-link-${a.identity_key}`}
                           className="block rounded border border-border-hairline hover:border-accent-teal/40 bg-bg-panel hover:bg-bg-panel-raised p-3 transition-colors duration-exec">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[9px] text-accent-teal font-bold border border-info-blue/40 bg-info-blue/10 px-1.5 py-0.5 rounded">
                              {channelNL}
                            </span>
                            <span className="text-xs font-semibold text-text-primary leading-snug">
                              {a.title || (a.body || {}).wat_ik_zie}
                            </span>
                          </div>
                          {tgt && (
                            <div className="text-[10px] text-text-muted mb-1"
                                 data-testid={`g4-advice-target-${a.identity_key}`}>
                              <span className="text-text-primary/85">{tgt.label}</span>
                              {tgt.path && (
                                <span className="ml-1 text-text-subtle">({tgt.path})</span>
                              )}
                            </div>
                          )}
                          {(a.body || {}).wat_ik_adviseer && (
                            <div className="text-[11px] text-text-muted mt-1 leading-snug">
                              {(a.body || {}).wat_ik_adviseer}
                            </div>
                          )}
                          <div className="mt-1.5 flex items-center gap-2">
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-bg-panel-raised text-text-muted">
                              Zekerheid: {ZEK_NL[zek] || "Middel"}
                            </span>
                            <span className="text-[9px] text-accent-teal ml-auto">
                              Bekijk waarom →
                            </span>
                          </div>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            {nexora_would_do_today.length > 0 && (
              <div className="mt-3 text-right">
                <button
                  onClick={() => nav("/ghost-ledger")}
                  data-testid="dashboard-open-ledger-btn"
                  className="text-[11px] text-accent-teal hover:text-accent-teal-dim transition-colors duration-exec"
                >
                  {t("dashboard.open_full_ledger")} →
                </button>
              </div>
            )}
          </Panel>

          {topOpp && (
            <div className="rounded-card border border-border-hairline bg-bg-panel p-5"
                  data-testid="dashboard-top-opportunity-detail">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-3.5 h-3.5 text-accent-teal" />
                <h3 className="text-[11px] text-text-muted font-semibold">
                  #1 KANS — VOLLEDIGE ANALYSE
                </h3>
              </div>
              <div className="text-lg font-display text-text-primary mb-3 tracking-tight">
                {topOpp.title}
              </div>
              <FivePartReasoning
                reasoning={topOpp.reasoning_summary || {}}
                confidence={topOpp.confidence != null
                  ? (topOpp.confidence >= 0.7 ? t("enum.confidence_pill.HIGH") : topOpp.confidence >= 0.5 ? t("enum.confidence_pill.MEDIUM") : t("enum.confidence_pill.LOW"))
                  : t("enum.confidence_pill.MEDIUM")}
                testidPrefix="top-opportunity"
              />
            </div>
          )}
        </div>
      </div>

      {/* Row 3 — Recente besluiten (tijdlijn) - ÉÉN duidelijk overzicht - */}
      <DecisionTimeline testid="dashboard-decision-timeline" />

      {/* M143 — Berichten-teaser -------------------------------------- */}
      <PostsTeaser onOpen={() => nav("/berichten")} />

      {/* M142 — Vlog-galerij teaser -------------------------------------- */}
      <VlogsTeaser onOpen={() => nav("/vlogs")} />

      {/* Weekly report shortcut ------------------------------------------ */}
      <button
        data-testid="dashboard-open-weekly-report"
        onClick={() => nav("/weekly-report")}
        className="w-full mt-4 mb-4 rounded-card border border-border-hairline bg-bg-panel hover:bg-bg-panel-raised transition-colors duration-exec px-5 py-3 flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-btn bg-bg-panel-raised border border-border-hairline flex items-center justify-center">
            <FileText className="w-3.5 h-3.5 text-accent-teal" />
          </div>
          <div>
            <div className="text-xs text-text-primary">{t("dashboard.open_weekly_report")}</div>
            <div className="text-[11px] text-text-muted">
              Samenvatting van deze week — belangrijkste aandachtspunten, kansen en prioriteiten.
            </div>
          </div>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-text-muted" />
      </button>

      {/* Ask JustEasySEO ------------------------------------------------------- */}
      <div className="rounded-card border border-border-hairline bg-bg-panel p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-3.5 h-3.5 text-accent-teal" />
          <h3 className="text-[11px] text-text-muted font-semibold">
            {t("dashboard.ask_title")}
          </h3>
        </div>
        <form onSubmit={submitAsk} className="flex gap-2">
          <input
            value={askInput}
            onChange={(e) => setAskInput(e.target.value)}
            placeholder={t("dashboard.ask_ph")}
            data-testid="ask-nexora-input"
            className="flex-1 h-9 px-3 rounded-btn bg-bg-canvas border border-border-hairline text-text-primary placeholder:text-text-subtle focus:outline-none focus:border-accent-teal transition-colors duration-exec text-sm"
          />
          <button
            type="submit" disabled={asking || !askInput.trim()}
            data-testid="ask-nexora-submit-btn"
            className="h-9 px-3 rounded-btn bg-accent-teal hover:bg-accent-teal-dim disabled:opacity-50 text-bg-canvas font-semibold flex items-center gap-2 text-xs transition-colors duration-exec"
          >
            {asking ? "…" : t("dashboard.ask_send")} <Send className="w-3 h-3" />
          </button>
        </form>
        {askResponse && <AskResponseCard resp={askResponse} onNav={(r) => nav(r)} />}
      </div>

      {/* Next step ------------------------------------------------------- */}
      {next_step && (
        <button
          onClick={() => next_step.route && nav(next_step.route)}
          data-testid="next-step-cta-button"
          className="w-full text-left rounded-card border border-accent-teal/30 bg-accent-teal/5 hover:bg-accent-teal/10 hover:border-accent-teal/60 p-5 transition-colors duration-exec"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] text-accent-teal font-bold mb-1.5">
                {t("dashboard.next_step_label")}
              </div>
              <div className="text-lg font-display text-text-primary mb-0.5 tracking-tight">
                {next_step.title_nl}
              </div>
              <p className="text-xs text-text-muted max-w-2xl"
                 data-testid="next-step-description">
                {next_step.description_nl}
                {next_step.fetched_at_iso && (
                  <> Laatst bijgewerkt op{" "}
                    <span data-testid="next-step-fetched-at">
                      {formatFetchedAt(next_step.fetched_at_iso, next_step.time_zone)}
                    </span>.</>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 text-accent-teal text-xs font-semibold shrink-0">
              {next_step.cta_nl} <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </button>
      )}
    </AppLayout>
  );
}

// ----------------------------------------------------------------------------
// KPI strip — Row 1
// ----------------------------------------------------------------------------
function KpiStrip({ pacing, budget, openOppsCount, ghostToday, healthScore }) {
  const cap = pacing?.cap_eur;
  const projected = pacing?.projected_eur;
  const isUnknown = pacing?.status === "UNKNOWN"
    || pacing?.spend_certainty === "UNKNOWN";
  const pct = !isUnknown && cap && projected != null
    ? Math.round((projected / cap) * 100) : null;
  const pacingHint = isUnknown
    ? "onbekend"
    : pacing?.only_ads
      ? "alleen Ads"
      : (pacing?.days_left != null
          ? t("dashboard.kpi_pacing_days").replace("{n}", String(pacing.days_left))
          : "—");
  const pacingValue = isUnknown
    ? "—"
    : (pct != null
        ? (pacing?.only_ads ? `Alleen Ads · ${pct}%` : `${pct}%`)
        : "—");
  const pacingTone = isUnknown
    ? "warn"
    : pacing?.status === "OK" ? "pos"
      : pacing?.status === "LET_OP" ? "warn"
        : pacing?.status === "WAARSCHUWING" || pacing?.status === "DREIGT_OVERSCHRIJDING" ? "neg"
          : "muted";
  const cells = [
    {
      label: t("dashboard.kpi_wallet_label"),
      value: cap != null ? _fmtEUR(cap) : "—",
      hint: pacing?.status === "NOT_CONFIGURED" ? t("dashboard.kpi_wallet_not_set") : t("dashboard.kpi_wallet_cap"),
    },
    {
      label: t("dashboard.kpi_pacing"),
      value: pacingValue,
      hint: pacingHint,
      tone: pacingTone,
      testid: "kpi-pacing",
    },
    {
      label: t("dashboard.kpi_opportunities"),
      value: String(openOppsCount ?? 0),
      hint: t("dashboard.kpi_opportunities_hint"),
    },
    {
      label: t("dashboard.kpi_today"),
      value: String(ghostToday ?? 0),
      hint: t("dashboard.kpi_today_hint"),
    },
    {
      label: t("dashboard.marketing_score_label"),
      value: healthScore != null ? String(healthScore) : "—",
      hint: healthScore != null ? " " : t("dashboard.marketing_score_not_ready"),
    },
  ];
  const toneCls = {
    pos:   "text-pos-green",
    warn:  "text-warn-amber",
    neg:   "text-neg-red",
    muted: "text-text-primary",
  };
  return (
    <div
      data-testid="dashboard-kpi-strip"
      className="mb-4 rounded-card border border-border-hairline bg-bg-panel"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-y sm:divide-y-0 sm:divide-x divide-border-hairline">
        {cells.map((c, i) => (
          <div key={i}
               data-testid={c.testid}
               className="px-4 py-3">
            <div className="text-[10px] text-text-muted"
                  style={{ letterSpacing: "0.08em" }}>
              {c.label}
            </div>
            <div className={`text-2xl mt-1 ${toneCls[c.tone] || toneCls.muted} num`}>
              {c.value}
            </div>
            {c.hint && (
              <div className="text-[10px] text-text-subtle mt-0.5">
                {c.hint}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Confirmed competitors — restyled to Fase-1 outline pills
// ----------------------------------------------------------------------------
function ConfirmedCompetitorsCard({ payload, onManage, onOpen }) {
  const items = (payload?.items || []).filter((c) => {
    const pt = c?.primary_classification;
    return pt === "DIRECT_BUSINESS_COMPETITOR" || pt === "PARTIAL_COMPETITOR";
  });
  const hasMore = !!payload?.has_more;
  const extra = Math.max(0, (payload?.total || items.length) - items.length);

  const BADGE = {
    DIRECT_BUSINESS_COMPETITOR: { label: t("dashboard.competitor_badge_direct"), color: "text-pos-green" },
    PARTIAL_COMPETITOR:         { label: t("dashboard.competitor_badge_partial"), color: "text-warn-amber" },
  };

  return (
    <div
      data-testid="dashboard-confirmed-competitors-card"
      className="rounded-card border border-border-hairline bg-bg-panel p-4"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-3.5 h-3.5 text-accent-teal" />
          <h3 className="text-[11px] text-text-muted font-semibold"
               style={{ letterSpacing: "0.08em" }}>
            {t("dashboard.confirmed_competitors_title")}
          </h3>
          {items.length > 0 && (
            <span className="text-[10px] text-text-subtle num">{items.length}</span>
          )}
        </div>
        <button
          type="button"
          onClick={onManage}
          data-testid="dashboard-confirmed-competitors-manage"
          className="text-[10px] text-accent-teal hover:text-accent-teal-dim transition-colors duration-exec"
        >
          {t("dashboard.confirmed_competitors_manage")} →
        </button>
      </div>
      {items.length === 0 ? (
        <div
          data-testid="dashboard-confirmed-competitors-empty"
          className="py-2 text-sm text-text-muted"
        >
          {t("dashboard.confirmed_competitors_empty")}
        </div>
      ) : (
        <ul className="space-y-1" data-testid="dashboard-confirmed-competitors-list">
          {items.map((c) => {
            const b = BADGE[c.primary_classification] || BADGE.DIRECT_BUSINESS_COMPETITOR;
            return (
              <li key={c.id} data-testid={`dashboard-confirmed-competitor-${c.id}`}>
                <button
                  type="button"
                  onClick={onOpen}
                  className="w-full flex items-center gap-3 flex-wrap px-2 py-1.5 rounded-btn border border-transparent hover:border-border-hairline hover:bg-bg-panel-raised text-left transition-colors duration-exec"
                >
                  <span className="text-sm text-text-primary truncate">{c.name}</span>
                  {c.domain && (
                    <span className="text-[11px] text-text-muted truncate">{c.domain}</span>
                  )}
                  <span className={`ml-auto pill-outline ${b.color}`}>{b.label}</span>
                </button>
              </li>
            );
          })}
          {(hasMore || extra > 0) && (
            <li>
              <button
                type="button"
                onClick={onManage}
                data-testid="dashboard-confirmed-competitors-more"
                className="text-[10px] text-accent-teal hover:text-accent-teal-dim px-2 py-1 transition-colors duration-exec"
              >
                {t("dashboard.confirmed_competitors_more").replace("{n}", String(extra || "?"))}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// M143 — Berichten (social posts) teaser.
// ----------------------------------------------------------------------------
function PostsTeaser({ onOpen }) {
  const [state, setState] = useState({ loaded: false, count: 0, thisWeek: 0 });
  useEffect(() => {
    api.get("/posts/gallery")
      .then((r) => {
        const items = r.data.items || [];
        // Bepaal huidige ISO-week label.
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        const days = Math.floor((now - start) / 86400000);
        const w = Math.ceil((days + start.getDay() + 1) / 7);
        const key = `${now.getFullYear()}-W${String(w).padStart(2, "0")}`;
        const thisWeek = items.filter(x => x.week_iso === key).length;
        setState({ loaded: true, count: items.length, thisWeek });
      })
      .catch(() => setState({ loaded: true, count: 0, thisWeek: 0 }));
  }, []);
  if (!state.loaded) return null;
  return (
    <button
      data-testid="dashboard-berichten-teaser"
      onClick={onOpen}
      className="w-full mt-4 rounded-card border border-border-hairline bg-bg-panel hover:bg-bg-panel-raised transition-colors duration-exec px-5 py-3 flex items-center justify-between gap-3 text-left"
    >
      <div className="flex items-center gap-3">
        <div className="w-7 h-7 rounded-btn bg-bg-panel-raised border border-border-hairline flex items-center justify-center">
          <MessageSquare className="w-3.5 h-3.5 text-accent-teal" />
        </div>
        <div>
          <div className="text-xs text-text-primary flex items-center gap-2">
            Berichten voor je socials
            <span className="text-[10px] text-accent-teal border border-accent-teal/30 bg-accent-teal/10 px-1.5 py-0.5 rounded"
                  data-testid="dashboard-berichten-teaser-count">
              {state.thisWeek > 0
                ? `${state.thisWeek} deze week`
                : (state.count > 0 ? `${state.count} gemaakt` : "Nog geen")}
            </span>
          </div>
          <div className="text-[11px] text-text-muted">
            {state.thisWeek >= 4
              ? "Je vier nieuwe posts staan klaar — kopieer en plaats zelf."
              : "JustEasySEO maakt kant-en-klare posts voor LinkedIn, Facebook, Instagram en TikTok."}
          </div>
        </div>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-text-muted" />
    </button>
  );
}

// ----------------------------------------------------------------------------
// M142 — Vlog-galerij teaser: kleine card die naar /vlogs linkt.
// Toont het aantal beschikbare vlogs en de nieuwste titel. Blijft zichtbaar
// ook als er nog geen vlogs zijn: JustEasySEO laat de gebruiker dan weten dat de
// galerij klaarstaat voor de eerste opname.
// ----------------------------------------------------------------------------
function VlogsTeaser({ onOpen }) {
  const [state, setState] = useState({ loaded: false, items: [], luma: false });
  useEffect(() => {
    api.get("/vlogs/gallery")
      .then((r) => setState({
        loaded: true,
        items: r.data.items || [],
        luma: !!r.data.luma_key_present,
      }))
      .catch(() => setState({ loaded: true, items: [], luma: false }));
  }, []);
  if (!state.loaded) return null;
  const count = state.items.length;
  const latest = state.items[0];
  return (
    <button
      data-testid="dashboard-vlogs-teaser"
      onClick={onOpen}
      className="w-full mt-4 rounded-card border border-border-hairline bg-bg-panel hover:bg-bg-panel-raised transition-colors duration-exec px-5 py-3 flex items-center justify-between gap-3 text-left"
    >
      <div className="flex items-center gap-3">
        <div className="w-7 h-7 rounded-btn bg-bg-panel-raised border border-border-hairline flex items-center justify-center">
          <Video className="w-3.5 h-3.5 text-accent-teal" />
        </div>
        <div>
          <div className="text-xs text-text-primary flex items-center gap-2">
            Vlog-galerij
            <span className="text-[10px] text-accent-teal border border-accent-teal/30 bg-accent-teal/10 px-1.5 py-0.5 rounded"
                  data-testid="dashboard-vlogs-teaser-count">
              {count} {count === 1 ? "vlog" : "vlogs"}
            </span>
          </div>
          <div className="text-[11px] text-text-muted">
            {count === 0
              ? "JustEasySEO maakt de vlog, jij plaatst 'm zelf online. Zodra er een klaarstaat vind je 'm hier."
              : (latest?.title
                  ? `Nieuwste: ${latest.title} — download en plaats zelf.`
                  : "JustEasySEO maakt de vlog, jij plaatst 'm zelf online.")}
          </div>
        </div>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-text-muted" />
    </button>
  );
}


// ----------------------------------------------------------------------------
// Competitor changes — commercial first, excluded dimmed
// ----------------------------------------------------------------------------
function CompetitorChangesPanel({ items, onOpenSnapshot, onViewAll }) {
  return (
    <div
      data-testid="dashboard-competitor-changes-panel"
      className="rounded-card border border-border-hairline bg-bg-panel p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Camera className="w-3.5 h-3.5 text-accent-teal" />
          <h3 className="text-[11px] text-text-muted font-semibold"
               style={{ letterSpacing: "0.08em" }}>
            {t("dashboard.competitor_changes_title")}
          </h3>
        </div>
      </div>
      {items.length === 0
        ? <EmptyState message={t("dashboard.competitor_changes_empty")} />
        : (
          <>
            <ul className="space-y-1.5" data-testid="dashboard-competitor-changes-list">
              {[...items].sort((a, b) =>
                  (b.is_commercial_competitor ? 1 : 0) - (a.is_commercial_competitor ? 1 : 0)
                ).map((ch) => {
                const activeTypes = ch.competitor_user_types_active || [];
                const isCommercial = !!ch.is_commercial_competitor;
                const isExcluded = ch.competitor_confirmation_status === "USER_EXCLUDED";
                let badgeLabel = null;
                let badgeColor = "text-text-muted";
                if (activeTypes.includes("DIRECT_BUSINESS_COMPETITOR")) {
                  badgeLabel = t("dashboard.competitor_badge_direct");
                  badgeColor = "text-pos-green";
                } else if (activeTypes.includes("PARTIAL_COMPETITOR")) {
                  badgeLabel = t("dashboard.competitor_badge_partial");
                  badgeColor = "text-warn-amber";
                } else if (isExcluded) {
                  badgeLabel = t("dashboard.competitor_badge_excluded");
                  badgeColor = "text-tier-c-grey";
                }
                return (
                  <li key={ch.id}
                       data-testid={`dashboard-competitor-change-${ch.id}`}
                       data-commercial={isCommercial ? "1" : "0"}
                       className={`rounded-btn border border-border-hairline ${isCommercial ? "bg-bg-panel-raised/60" : "bg-bg-panel opacity-60"}`}>
                    <button
                      type="button"
                      onClick={() => onOpenSnapshot(ch.competitor_id)}
                      className="w-full text-left px-3 py-2 hover:bg-bg-panel-raised transition-colors duration-exec"
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="pill-outline text-info-blue">{ch.change_type}</span>
                        {badgeLabel && (
                          <span
                            data-testid={`dashboard-competitor-change-badge-${ch.id}`}
                            className={`pill-outline ${badgeColor}`}>
                            {badgeLabel}
                          </span>
                        )}
                        <span className="text-[10px] text-text-subtle ml-auto num">
                          {new Date(ch.detected_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                      </div>
                      <p className="text-xs text-text-primary/90 leading-snug">{ch.summary_nl}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 text-right">
              <button
                onClick={onViewAll}
                data-testid="dashboard-competitor-changes-view-all"
                className="text-[10px] text-accent-teal hover:text-accent-teal-dim transition-colors duration-exec"
              >
                {t("dashboard.competitor_changes_view_all")} →
              </button>
            </div>
          </>
        )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Wallet pacing card
// ----------------------------------------------------------------------------
function PacingCard({ pacing, onNav }) {
  if (!pacing) return null;
  const s = pacing.status;
  if (s === "NOT_CONFIGURED") {
    return (
      <button
        data-testid="pacing-card"
        onClick={onNav}
        className="w-full mb-4 text-left rounded-card border border-dashed border-border-hairline bg-bg-panel hover:bg-bg-panel-raised transition-colors duration-exec px-4 py-2.5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <Gauge className="w-3.5 h-3.5 text-text-subtle" />
          {t("dashboard.pacing_not_configured")}
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-text-subtle" />
      </button>
    );
  }
  // M128 — bij UNKNOWN of alleen-Ads met CONFIRMED_PARTIAL rendert de
  // banner nooit "loopt binnen bereik". Alleen-Ads krijgt een eigen
  // amber banner met `message_nl` uit de backend.
  const certainty = pacing.spend_certainty
    || (pacing.projected_eur == null ? "UNKNOWN" : "CONFIRMED");
  if (certainty === "UNKNOWN") {
    return (
      <button
        data-testid="pacing-card"
        data-status="UNKNOWN"
        onClick={onNav}
        className="w-full mb-4 text-left rounded-card border border-warn-amber/40 bg-warn-amber/5 hover:bg-warn-amber/10 transition-colors duration-exec px-4 py-2.5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Gauge className="w-3.5 h-3.5 text-warn-amber shrink-0" />
          <span className="text-xs text-warn-amber truncate">
            {pacing.message_nl
              || "Onvoldoende gegevens om maandritme te berekenen."}
          </span>
        </div>
        <ChevronRight className="w-3.5 h-3.5 shrink-0 text-warn-amber" />
      </button>
    );
  }
  if (certainty === "CONFIRMED_PARTIAL" || pacing.only_ads) {
    return (
      <button
        data-testid="pacing-card"
        data-status={pacing.status || "ONLY_ADS"}
        onClick={onNav}
        className="w-full mb-4 text-left rounded-card border border-warn-amber/40 bg-warn-amber/5 hover:bg-warn-amber/10 transition-colors duration-exec px-4 py-3 flex items-start justify-between gap-3"
      >
        <div className="flex items-start gap-3 min-w-0">
          <Gauge className="w-3.5 h-3.5 text-warn-amber shrink-0 mt-0.5" />
          <div className="text-xs text-warn-amber space-y-0.5 min-w-0">
            {pacing.line1_nl && (
              <div data-testid="pacing-line-1" className="truncate">
                {pacing.line1_nl}
              </div>
            )}
            {pacing.line2_nl && (
              <div data-testid="pacing-line-2" className="truncate">
                {pacing.line2_nl}
              </div>
            )}
            {pacing.line3_nl && (
              <div data-testid="pacing-line-3" className="text-[10px] text-text-muted">
                {pacing.line3_nl}
              </div>
            )}
          </div>
        </div>
        <ChevronRight className="w-3.5 h-3.5 shrink-0 text-warn-amber mt-0.5" />
      </button>
    );
  }
  const pct = pacing.cap_eur > 0 ? Math.round((pacing.projected_eur / pacing.cap_eur) * 100) : 0;
  const projected = _fmtEUR(pacing.projected_eur);
  const cap = _fmtEUR(pacing.cap_eur);
  const CARDS = {
    OK: {
      border: "border-pos-green/40",
      bg: "bg-pos-green/5",
      icon: "text-pos-green",
      text: "text-pos-green",
      msg: t("dashboard.pacing_ok"),
    },
    LET_OP: {
      border: "border-warn-amber/40", bg: "bg-warn-amber/5",
      icon: "text-warn-amber", text: "text-warn-amber",
      msg: t("dashboard.pacing_let_op")
        .replace("{projected}", projected).replace("{cap}", cap)
        .replace("{pct}", String(pct)).replace("{days}", String(pacing.days_left)),
    },
    WAARSCHUWING: {
      border: "border-warn-amber/50", bg: "bg-warn-amber/10",
      icon: "text-warn-amber", text: "text-warn-amber",
      msg: t("dashboard.pacing_warn")
        .replace("{projected}", projected).replace("{cap}", cap)
        .replace("{pct}", String(pct)).replace("{days}", String(pacing.days_left)),
    },
    DREIGT_OVERSCHRIJDING: {
      border: "border-neg-red/40", bg: "bg-neg-red/10",
      icon: "text-neg-red", text: "text-neg-red",
      msg: t("dashboard.pacing_over").replace("{projected}", projected).replace("{cap}", cap),
    },
  };
  const st = CARDS[s] || CARDS.OK;
  return (
    <button
      data-testid="pacing-card"
      data-status={s}
      onClick={onNav}
      className={`w-full mb-4 text-left rounded-card border ${st.border} ${st.bg} transition-colors duration-exec px-4 py-2.5 flex items-center justify-between gap-3 hover:brightness-110`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Gauge className={`w-3.5 h-3.5 shrink-0 ${st.icon}`} />
        <span className={`text-xs ${st.text} truncate`}>{st.msg}</span>
      </div>
      <ChevronRight className="w-3.5 h-3.5 shrink-0 text-text-muted" />
    </button>
  );
}

// ----------------------------------------------------------------------------
// Ghost Ledger dense table strip — Row 3
// ----------------------------------------------------------------------------
function GhostLedgerStrip({ items, onOpen }) {
  return (
    <div
      data-testid="dashboard-ghost-decisions-panel"
      className="rounded-card border border-border-hairline bg-bg-panel"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border-hairline">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-accent-teal" />
          <h3 className="text-[11px] text-text-muted font-semibold"
               style={{ letterSpacing: "0.08em" }}>
            {t("dashboard.ghost_decisions")}
          </h3>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="text-[10px] text-accent-teal hover:text-accent-teal-dim transition-colors duration-exec"
        >
          {t("dashboard.kpi_all")} →
        </button>
      </div>
      {items.length === 0 ? (
        <EmptyState message={t("dashboard.ghost_decisions_empty")} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-text-subtle border-b border-border-hairline">
                <th className="text-left px-4 py-2 font-medium">{t("ledger.col_time")}</th>
                <th className="text-left px-4 py-2 font-medium">{t("ledger.col_type")}</th>
                <th className="text-left px-4 py-2 font-medium">{t("ledger.col_channel")}</th>
                <th className="text-left px-4 py-2 font-medium">{t("ledger.col_impact")}</th>
                <th className="text-left px-4 py-2 font-medium">{t("ledger.col_status")}</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 6).map((g) => {
                const range = g.opportunity && g.opportunity.expected_return_low_eur != null
                  ? `${_fmtEUR(g.opportunity.expected_return_low_eur)} – ${_fmtEUR(g.opportunity.expected_return_high_eur)}`
                  : "—";
                return (
                  <tr key={g.id}
                       data-testid={`ghost-decision-item-${g.id}`}
                       className="border-b border-border-hairline/60 hover:bg-bg-panel-raised transition-colors duration-exec">
                    <td className="px-4 py-2 text-text-muted num whitespace-nowrap">
                      {g.created_at ? new Date(g.created_at).toLocaleString("nl-NL",
                        { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-4 py-2 text-text-primary">{g.action_type}</td>
                    <td className="px-4 py-2 text-text-muted">{g.channel || "—"}</td>
                    <td className="px-4 py-2 text-text-primary num">{range}</td>
                    <td className="px-4 py-2">
                      <span className="pill-outline text-info-blue">{t(`enum.outcome.${g.outcome || "PROPOSED"}`)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Would-do item — expanded FivePartReasoning + DecisionCard
// ----------------------------------------------------------------------------
function WouldDoItem({ g }) {
  const [open, setOpen] = useState(false);
  const [card, setCard] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const confLabel = g.opportunity && g.opportunity.confidence
    ? (g.opportunity.confidence >= 0.7 ? t("enum.confidence_pill.HIGH")
        : g.opportunity.confidence >= 0.5 ? t("enum.confidence_pill.MEDIUM") : t("enum.confidence_pill.LOW"))
    : t("enum.confidence_pill.LOW");

  const openCard = async () => {
    setShowCard((v) => !v);
    if (card || loadingCard) return;
    setLoadingCard(true);
    try {
      const { data } = await api.get(`/ghost-ledger/${g.id}/decision-card`);
      setCard(data);
    } catch (e) {
      setCard({ error: e?.response?.data?.detail || t("common.error_generic") });
    } finally { setLoadingCard(false); }
  };

  return (
    <li data-testid={`would-do-item-${g.id}`}
         className="rounded-btn border border-border-hairline bg-bg-panel-raised/60 hover:bg-bg-panel-raised transition-colors duration-exec">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`would-do-toggle-${g.id}`}
        className="w-full text-left px-3 py-2.5"
      >
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-sm text-text-primary mb-0.5">{g.title}</div>
            <div className="text-[10px] text-text-muted flex items-center gap-2 flex-wrap">
              <span>{formatEnum("action_type", g.action_type, g.action_type)}</span>
              <span className="text-text-subtle">·</span>
              <span>{g.execution_mode === "GHOST" ? "Adviesmodus" : g.execution_mode}</span>
              {g.evidence_class && (
                <span className="pill-outline text-info-blue">
                  {formatEnum("evidence", g.evidence_class, g.evidence_class)}
                </span>
              )}
            </div>
            {g.reasoning_summary?.expected_impact_nl && (
              <div className="text-xs text-text-primary/90 mt-1.5 leading-relaxed flex items-start gap-2">
                <span className="pill-outline text-warn-amber shrink-0">
                  {t("ledger.hypothesis_label")}
                </span>
                <span>{g.reasoning_summary.expected_impact_nl}</span>
              </div>
            )}
          </div>
          <div className="text-text-muted pt-0.5 shrink-0">
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </div>
        </div>
      </button>
      <div className="px-3 pb-2 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={openCard}
          data-testid={`would-do-decision-card-btn-${g.id}`}
          className="text-[10px] text-accent-teal hover:text-accent-teal-dim transition-colors duration-exec"
        >
          {showCard ? t("dashboard.hide_decision") : t("dashboard.view_decision")}
        </button>
      </div>
      {showCard && (
        <div className="px-3 pb-3 border-t border-border-hairline pt-3" data-testid={`would-do-decision-card-${g.id}`}>
          {loadingCard ? (
            <div className="text-xs text-text-muted italic">{t("common.loading")}</div>
          ) : card?.error ? (
            <div className="text-xs text-neg-red">{card.error}</div>
          ) : card ? (
            <DecisionCard card={card} compact />
          ) : null}
        </div>
      )}
      {open && (
        <div className="px-3 pb-3 border-t border-border-hairline pt-3"
              data-testid={`would-do-detail-${g.id}`}>
          <FivePartReasoning
            reasoning={g.reasoning_summary_five_step || {}}
            confidence={confLabel}
            scoreBreakdown={g.opportunity?.opportunity_score_breakdown}
            compact
            testidPrefix={`would-do-reasoning-${g.id}`}
          />
        </div>
      )}
    </li>
  );
}

// ----------------------------------------------------------------------------
// Ask JustEasySEO response card
// ----------------------------------------------------------------------------
const FIVE_PART_HEADERS_NL = [
  "Wat ik zie",
  "Waarom dit belangrijk is",
  "Wat ik adviseer",
  "Wat het waarschijnlijk kost of oplevert",
  "Zekerheid",
];

// M128 eindronde — legacy-koppen voor `parseFivePart`-fallback op oude
// cache-antwoorden. "Zekerheid" (huidig), "Hoe zeker ik ben" (M128 G4-tussen),
// "Confidence" (pre-G4). Volgorde: nieuw eerst.
const CONFIDENCE_HEADER_ALIASES = ["Zekerheid", "Hoe zeker ik ben", "Confidence"];

function parseFivePart(answerText) {
  if (!answerText) return null;
  // Voor de zekerheid-kop: probeer alle aliassen (Zekerheid → Hoe zeker
  // ik ben → Confidence). Eerste hit wint zodat oude cache-antwoorden
  // met "Hoe zeker ik ben" / "Confidence" nog steeds parsen.
  const findConfHeader = () => {
    for (const alias of CONFIDENCE_HEADER_ALIASES) {
      if (answerText.indexOf(`**${alias}**`) >= 0) return alias;
    }
    return "Zekerheid";
  };
  const confHeader = findConfHeader();
  const headers = FIVE_PART_HEADERS_NL.map(
    (h) => (h === "Zekerheid" ? confHeader : h));
  const positions = headers.map((h) => {
    const marker = `**${h}**`;
    return { h, marker, idx: answerText.indexOf(marker) };
  });
  if (positions.some((p) => p.idx < 0)) return null;
  const ordered = [...positions].sort((a, b) => a.idx - b.idx);
  const sections = ordered.map((p, i) => {
    const start = p.idx + p.marker.length;
    const end = i + 1 < ordered.length ? ordered[i + 1].idx : answerText.length;
    // Normaliseer legacy zekerheid-kop naar "Zekerheid" in de output.
    const displayHeader = CONFIDENCE_HEADER_ALIASES.includes(p.h) ? "Zekerheid" : p.h;
    return { header: displayHeader, body: answerText.slice(start, end).trim() };
  });
  return FIVE_PART_HEADERS_NL.map(
    (h) => sections.find((s) => s.header === h));
}

function AskResponseCard({ resp, onNav }) {
  const answerText = resp.answer || "";
  const evid = Array.isArray(resp.evidence) ? resp.evidence : [];
  const assumptions = Array.isArray(resp.assumptions) ? resp.assumptions : [];
  const bronnen = Array.isArray(resp.bronnen) ? resp.bronnen : [];
  const [showEvidence, setShowEvidence] = useState(false);
  // M128 eindronde — leest UITSLUITEND uit gestructureerde velden zodat
  // LLM-hallucinaties op koppen ("Wat ic zie") onmogelijk zijn. De 5
  // NL-koppen komen uit JSX-constanten, niet uit LLM-output. Fallback
  // op parseFivePart(answer) voor oudere responses zonder structured.
  const stripMd = (s) => (typeof s === "string"
    ? s.replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/^\s*#{1,6}\s+/gm, "")
        .trim()
    : s);
  // M135-5b — behoud markdown-links `[label](path)` als klikbare React
  // Router-navigatie ipv de link te strippen. Interne routes (`/publish-
  // queue?...`) worden via `onNav` afgehandeld; externe URLs openen in
  // een nieuw tabblad.
  const renderInlineMd = (s) => {
    if (typeof s !== "string" || !s) return s;
    const cleaned = s.replace(/\*\*(.*?)\*\*/g, "$1")
                     .replace(/^\s*#{1,6}\s+/gm, "");
    const parts = [];
    const rx = /\[([^\]]+)\]\(([^)]+)\)/g;
    let last = 0;
    let m;
    let idx = 0;
    while ((m = rx.exec(cleaned)) !== null) {
      if (m.index > last) parts.push(cleaned.slice(last, m.index));
      const label = m[1];
      const href = m[2];
      const isInternal = href.startsWith("/");
      parts.push(
        isInternal ? (
          <button
            key={`lnk-${idx++}`}
            data-testid="ask-inline-link"
            onClick={() => onNav(href)}
            className="text-accent-teal hover:underline font-medium inline"
          >{label}</button>
        ) : (
          <a key={`lnk-${idx++}`} href={href} target="_blank" rel="noreferrer"
              className="text-accent-teal hover:underline font-medium">{label}</a>
        )
      );
      last = m.index + m[0].length;
    }
    if (last < cleaned.length) parts.push(cleaned.slice(last));
    return parts;
  };
  const structured = {
    wat_ik_zie: stripMd(resp.wat_ik_zie || ""),
    waarom_belangrijk: stripMd(resp.waarom_belangrijk || ""),
    wat_ik_adviseer: stripMd(resp.wat_ik_adviseer || ""),
    waarschijnlijke_kosten_opbrengst: stripMd(resp.waarschijnlijke_kosten_opbrengst || ""),
    // M128 eindronde — kop hernoemd "Hoe zeker ik ben" → "Zekerheid".
    // Backend levert `zekerheid`; legacy cache-antwoorden hebben nog
    // `hoe_zeker_ik_ben` — beide gehonoreerd zodat niets breekt.
    zekerheid: stripMd(resp.zekerheid || resp.hoe_zeker_ik_ben || ""),
  };
  const hasStructured = Object.values(structured).some((v) => v && v.length > 0);
  const legacyFive = hasStructured ? null : parseFivePart(answerText);
  const isAdvice = hasStructured || Boolean(legacyFive);
  const FIVE = [
    { key: "wat_ik_zie", header: "Wat ik zie" },
    { key: "waarom_belangrijk", header: "Waarom dit belangrijk is" },
    { key: "wat_ik_adviseer", header: "Wat ik adviseer" },
    { key: "waarschijnlijke_kosten_opbrengst", header: "Wat het waarschijnlijk kost of oplevert" },
    { key: "zekerheid", header: "Zekerheid" },
  ];
  return (
    <div data-testid="ask-nexora-response"
          className="mt-3 p-4 rounded-btn border border-border-hairline bg-bg-panel-raised text-sm text-text-primary leading-relaxed">
      {isAdvice ? (
        <div className="space-y-3" data-testid="ask-nexora-five-part">
          {(hasStructured ? FIVE : []).map((s, i) => {
            const body = structured[s.key] || "";
            if (!body) return null;
            const isHypo = s.key === "waarschijnlijke_kosten_opbrengst";
            return (
              <div key={s.key} data-testid={`ask-five-part-${s.key}`}
                    className="border-l-2 border-accent-teal/60 pl-3">
                <div className="text-[10px] text-text-muted font-semibold mb-1">
                  {s.header}
                  {isHypo && (
                    <span className="ml-2 pill-outline text-warn-amber">Vermoeden</span>
                  )}
                </div>
                <div className="text-sm text-text-primary whitespace-pre-wrap">{renderInlineMd(body)}</div>
              </div>
            );
          })}
          {legacyFive && legacyFive.map((s, i) => {
            if (!s) return null;
            const isHypo = s.header === "Wat het waarschijnlijk kost of oplevert";
            return (
              <div key={i} data-testid={`ask-five-part-legacy-${i}`}
                    className="border-l-2 border-accent-teal/60 pl-3">
                <div className="text-[10px] text-text-muted font-semibold mb-1">
                  {s.header}
                  {isHypo && (
                    <span className="ml-2 pill-outline text-warn-amber">Vermoeden</span>
                  )}
                </div>
                <div className="text-sm text-text-primary whitespace-pre-wrap">{stripMd(s.body)}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="whitespace-pre-wrap text-text-primary">{stripMd(answerText)}</div>
      )}
      {resp.confidence && (
        <div className="mt-3 text-[10px] text-text-muted">
          Zekerheid: <span className="text-text-primary">{resp.confidence}</span>
        </div>
      )}
      {resp.next_action?.route && (
        <button
          onClick={() => onNav(resp.next_action.route)}
          data-testid="ask-nexora-next-action"
          className="mt-3 text-xs font-semibold text-accent-teal hover:text-accent-teal-dim transition-colors duration-exec"
        >
          → {resp.next_action.label_nl}
        </button>
      )}
      {(evid.length > 0 || bronnen.length > 0 || assumptions.length > 0) && (
        <div className="mt-3 pt-3 border-t border-border-hairline">
          <button
            onClick={() => setShowEvidence((v) => !v)}
            data-testid="ask-nexora-evidence-toggle"
            className="text-[10px] text-accent-teal hover:text-accent-teal-dim flex items-center gap-1 transition-colors duration-exec"
          >
            <Eye className="w-3 h-3" /> {showEvidence ? t("common.hide_evidence") : t("common.view_evidence")} ({evid.length + bronnen.length})
          </button>
          {showEvidence && (
            <div className="mt-2 space-y-2">
              {bronnen.map((b, i) => (
                <div key={`b-${i}`} className="text-xs text-text-primary border-l-2 border-border-hairline pl-2">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="pill-filled">{b.bron}</span>
                    <span className="text-text-muted">{b.referentie}</span>
                  </div>
                  {b.citaat && <div>{stripMd(b.citaat)}</div>}
                </div>
              ))}
              {evid.map((e, i) => (
                <div key={`e-${i}`} className="text-xs text-text-primary border-l-2 border-border-hairline pl-2">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="pill-outline text-info-blue">{e.type}</span>
                    <span className="pill-filled">{e.source}</span>
                  </div>
                  <div>{stripMd(e.snippet || e.reference || "")}</div>
                </div>
              ))}
              {assumptions.length > 0 && (
                <div className="text-xs text-text-muted">
                  <span className="font-semibold text-text-primary">Aannames: </span>
                  {assumptions.join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Panel + EmptyState helpers
// ----------------------------------------------------------------------------
function Panel({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-card border border-border-hairline bg-bg-panel p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5 text-accent-teal" />
        <h3 className="text-[11px] text-text-muted font-semibold"
             style={{ letterSpacing: "0.08em" }}>
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="py-6 text-sm text-text-muted">{message}</div>
  );
}
